import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { API_BASE } from '../config';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';
import { DialogFooter, DialogPrimaryShortcut } from './ui/dialog';
import { RichTextField, type MentionableEntity } from './RichTextField';
import { RelatedEntityLink } from './RelatedEntityLink';
import { removeMentionFromHtml } from '../utils/entity-utils';
import { mapMentionableEntitiesFromApiResponse } from '../utils/mentionable-entities';
import { dispatchMentionableEntitiesChangedForDocument } from '../utils/mentionable-entities-events';
import {
  fetchMentionRenameImpact,
  type MentionRenameImpactSource,
} from '../utils/mention-rename';
import { MentionRenameImpactList } from './MentionRenameImpactList';
import { useToast } from '../contexts/ToastContext';
import { readJsonWithOnboarding } from '../onboarding/notify-onboarding-update';
import { Input } from './ui/input';
import { InlineEditableText } from './InlineEditableText';
import { EditDialogFooter } from './EditDialogFooter';
import { EntityDetailModalLayout, EntityDetailModalTitle } from './EntityDetailModalLayout';
import { ConfirmDialog } from './ConfirmDialog';
import { AnalyzeButton } from './AnalyzeButton';
import { CharacterChat } from './CharacterChat';
import { LinkedImageResourcesCarousel } from './LinkedImageResourcesCarousel';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { EntityRelevanceDisplay } from './EntityRelevanceDisplay';
import { EntityRelevanceEditor } from './EntityRelevanceEditor';
import { Badge } from './ui/badge';

type CharacterPanelView = 'detail' | 'chat';
import {
  clampEntityRelevance,
  defaultCreateEntityRelevance,
  readLastCreatedCharacterRelevance,
  writeLastCreatedCharacterRelevance,
} from '../utils/entity-relevance';

interface Character {
  id: string;
  name: string;
  description?: string;
  role?: string;
  appearance?: string;
  personality?: string;
  background?: string;
  relationships?: string | Record<string, string>;
  notes?: string;
  inconsistencies?: string;
  improvements?: string;
  speechStyle?: string;
  mannerisms?: string;
  beliefsAndAttitude?: string;
  informationAccess?: string;
  isManual?: boolean;
  userNotes?: string;
  relevance?: number;
  mainImageResourceId?: string | null;
}

type EditableCharacterField =
  | 'role'
  | 'appearance'
  | 'personality'
  | 'background'
  | 'relationships'
  | 'notes'
  | 'inconsistencies'
  | 'improvements'
  | 'speechStyle'
  | 'mannerisms'
  | 'beliefsAndAttitude'
  | 'informationAccess';

function normalizeRichTextDescription(html: string | undefined): string | undefined {
  const s = (html ?? '').trim();
  if (!s) return undefined;
  const emptyTags = ['<p></p>', '<p><br></p>', '<p><br/></p>'];
  if (emptyTags.includes(s)) return undefined;
  return s;
}

function stripHtml(html: string): string {
  if (!html || !html.trim()) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent ?? div.innerText ?? '').trim();
}

function formatAnalysisPlainText(text: string): string {
  return text.replace(/\\n/g, '\n');
}

function parseRelationships(
  relationships: Character['relationships'],
): { asObject: Record<string, string> | null; asString: string | null } {
  if (!relationships) {
    return { asObject: null, asString: null };
  }
  if (typeof relationships === 'object' && !Array.isArray(relationships)) {
    return { asObject: relationships, asString: null };
  }
  if (typeof relationships === 'string') {
    try {
      const parsed = JSON.parse(relationships);
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        return { asObject: parsed as Record<string, string>, asString: null };
      }
    } catch {
      // plain string
    }
    return { asObject: null, asString: relationships };
  }
  return { asObject: null, asString: String(relationships) };
}

interface CharacterDetailProps {
  documentId?: string;
  projectId?: string;
  characterId?: string | null;
  onClose?: () => void;
  isModal?: boolean;
  initialName?: string;
  onCreate?: (character: Character) => void;
  onUpdated?: (character: Character) => void;
  onDeleted?: (characterId: string) => void;
  scrollToSection?: 'related_entities';
}

export const CharacterDetail: React.FC<CharacterDetailProps> = ({
  documentId: propDocumentId,
  projectId: propProjectId,
  characterId: propCharacterId,
  onClose,
  isModal = false,
  initialName,
  onCreate,
  onUpdated,
  onDeleted,
  scrollToSection,
}) => {
  const params = useParams<{ projectId?: string; documentId: string; characterId: string }>();
  const routeDocumentId = params.documentId;
  const routeProjectId = params.projectId;
  const routeCharacterId = params.characterId;
  const documentId = propDocumentId || routeDocumentId;
  const projectId = propProjectId || routeProjectId;
  const characterId = propCharacterId === undefined ? routeCharacterId : propCharacterId;
  const isCreateMode = isModal && !characterId;
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t } = useLocale();
  const relatedEntitiesSectionRef = useRef<HTMLDivElement>(null);
  const [masterKey] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('masterKey');
  });
  const [shareToken] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('shareToken');
  });
  const [character, setCharacter] = useState<Character | null>(null);
  const [allCharacters, setAllCharacters] = useState<Character[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [documentTitle, setDocumentTitle] = useState<string>('');
  const [analyzing, setAnalyzing] = useState(false);
  const [panelView, setPanelView] = useState<CharacterPanelView>('detail');
  const panelScrollPositionsRef = useRef<Record<CharacterPanelView, number>>({
    detail: 0,
    chat: 0,
  });
  const panelBodyRef = useRef<HTMLDivElement>(null);
  const [chatChromeEl, setChatChromeEl] = useState<HTMLDivElement | null>(null);
  const [createName, setCreateName] = useState(initialName ?? '');
  const [createUserNotes, setCreateUserNotes] = useState('');
  const [createRelevance, setCreateRelevance] = useState(() =>
    defaultCreateEntityRelevance(
      documentId ? readLastCreatedCharacterRelevance(documentId) : undefined
    )
  );
  const [creating, setCreating] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [editDescriptionValue, setEditDescriptionValue] = useState('');
  const [editNotesValue, setEditNotesValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingAnalysisField, setSavingAnalysisField] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [renameImpactOpen, setRenameImpactOpen] = useState(false);
  const [pendingRenameName, setPendingRenameName] = useState<string | null>(null);
  const [renameImpactSources, setRenameImpactSources] = useState<MentionRenameImpactSource[]>([]);
  const [renameImpactLoading, setRenameImpactLoading] = useState(false);
  const [mentionableEntities, setMentionableEntities] = useState<MentionableEntity[]>([]);
  const [entityRefs, setEntityRefs] = useState<Array<{
    sourceType: string;
    sourceId: string;
    sourceDocumentId: string | null;
    targetType: string;
    targetId: string;
    targetDocumentId: string | null;
  }>>([]);
  const [entityRefsRefreshTrigger, setEntityRefsRefreshTrigger] = useState(0);
  const { showToast, showErrorToast, showFieldSavedToast } = useToast();
  const [resolvedProjectId, setResolvedProjectId] = useState<string | null>(null);

  useEffect(() => {
    if (!isCreateMode || initialName == null) return;
    setCreateName(initialName);
  }, [isCreateMode, initialName]);

  useEffect(() => {
    setPanelView('detail');
    panelScrollPositionsRef.current = { detail: 0, chat: 0 };
  }, [characterId]);

  const getPanelScrollParent = useCallback((): HTMLElement | null => {
    const body = panelBodyRef.current;
    if (!body) {
      return null;
    }
    let node: HTMLElement | null = body.parentElement;
    while (node) {
      const overflowY = window.getComputedStyle(node).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }, []);

  const switchPanelView = useCallback(
    (nextView: CharacterPanelView) => {
      if (nextView === panelView) {
        return;
      }
      const scrollParent = getPanelScrollParent();
      if (scrollParent) {
        panelScrollPositionsRef.current[panelView] = scrollParent.scrollTop;
      }
      setPanelView(nextView);
      requestAnimationFrame(() => {
        const nextScrollParent = getPanelScrollParent();
        if (nextScrollParent) {
          nextScrollParent.scrollTop = panelScrollPositionsRef.current[nextView] ?? 0;
        }
      });
    },
    [getPanelScrollParent, panelView]
  );

  useEffect(() => {
    const fetchDocumentTitle = async (): Promise<void> => {
      if ((!token && !masterKey) || !documentId) {
        return;
      }

      try {
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (response.ok) {
          const data = await response.json();
          setDocumentTitle(data.title || '');
        }
      } catch (err) {
        console.error('Error fetching document title:', err);
      }
    };

    fetchDocumentTitle();
  }, [token, documentId, masterKey]);

  useEffect(() => {
    const authHeaders: Record<string, string> = token && !masterKey ? { Authorization: `Bearer ${token}` } : {};

    const fetchMentionableEntities = async (targetProjectId: string): Promise<void> => {
      try {
        const res = await fetch(`${API_BASE}/api/projects/${targetProjectId}/mentionable-entities`, {
          headers: { ...authHeaders },
        });
        if (res.ok) {
          const data = await res.json();
          setMentionableEntities(mapMentionableEntitiesFromApiResponse(data));
        } else {
          setMentionableEntities([]);
        }
      } catch {
        setMentionableEntities([]);
      }
    };

    const fetchEntityRefs = async (targetProjectId: string): Promise<void> => {
      try {
        const res = await fetch(`${API_BASE}/api/projects/${targetProjectId}/entity-references`, {
          headers: { ...authHeaders },
        });
        if (res.ok) {
          const data = await res.json();
          setEntityRefs(data.references ?? []);
        } else {
          setEntityRefs([]);
        }
      } catch {
        setEntityRefs([]);
      }
    };

    const run = async (): Promise<void> => {
      if (!token && !masterKey) return;

      if (projectId) {
        setResolvedProjectId(projectId);
        fetchMentionableEntities(projectId);
        fetchEntityRefs(projectId);
        return;
      }

      if (!documentId) return;

      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}`;
      const res = await fetch(url, {
        headers: { ...authHeaders },
      });
      if (!res.ok) return;
      const data = await res.json();
      const docProjectId = data.projectId;
      setResolvedProjectId(docProjectId ?? null);
      if (docProjectId) {
        fetchMentionableEntities(docProjectId);
        fetchEntityRefs(docProjectId);
      } else {
        setMentionableEntities([]);
        setEntityRefs([]);
      }
    };

    run();
  }, [projectId, documentId, token, masterKey, entityRefsRefreshTrigger]);

  useEffect(() => {
    if (isCreateMode) {
      setLoading(false);
      return;
    }
  }, [isCreateMode]);

  useEffect(() => {
    const fetchCharacter = async (): Promise<void> => {
      if ((!token && !masterKey) || !documentId || !characterId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/characters?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/characters`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch characters');
        }

        const data = await response.json();

        let charactersData: Character[] = [];

        if (Array.isArray(data)) {
          charactersData = data;
        } else if (data.characters) {
          charactersData = data.characters;
        } else if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
          const keys = Object.keys(data);
          if (keys.length > 0 && typeof data[keys[0]] === 'object') {
            charactersData = Object.entries(data).map(([name, char]: [string, any]) => {
              if (typeof char === 'object' && char !== null) {
                return { name, ...char };
              }
              return { name, description: String(char) };
            });
          }
        }

        if (!Array.isArray(charactersData)) {
          charactersData = [];
        }

        charactersData = charactersData.map((char) => ({
          id: char.id || `temp-${Math.random().toString(36).substr(2, 9)}`,
          name: String(char.name || ''),
          description: char.description ? (typeof char.description === 'object' ? JSON.stringify(char.description) : String(char.description)) : undefined,
          role: char.role ? (typeof char.role === 'object' ? JSON.stringify(char.role) : String(char.role)) : undefined,
          appearance: char.appearance ? (typeof char.appearance === 'object' ? JSON.stringify(char.appearance) : String(char.appearance)) : undefined,
          personality: char.personality ? (typeof char.personality === 'object' ? JSON.stringify(char.personality) : String(char.personality)) : undefined,
          background: char.background ? (typeof char.background === 'object' ? JSON.stringify(char.background) : String(char.background)) : undefined,
          relationships: char.relationships
            ? (typeof char.relationships === 'object' && char.relationships !== null && !Array.isArray(char.relationships)
              ? char.relationships
              : typeof char.relationships === 'string'
                ? (() => {
                  try {
                    const parsed = JSON.parse(char.relationships);
                    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? parsed : char.relationships;
                  } catch {
                    return char.relationships;
                  }
                })()
                : String(char.relationships))
            : undefined,
          notes: char.notes ? (typeof char.notes === 'object' ? JSON.stringify(char.notes) : String(char.notes)) : undefined,
          userNotes: char.userNotes ? (typeof char.userNotes === 'object' ? JSON.stringify(char.userNotes) : String(char.userNotes)) : undefined,
          inconsistencies: char.inconsistencies ? String(char.inconsistencies) : undefined,
          improvements: char.improvements ? String(char.improvements) : undefined,
          speechStyle: char.speechStyle ? String(char.speechStyle) : undefined,
          mannerisms: char.mannerisms ? String(char.mannerisms) : undefined,
          beliefsAndAttitude: char.beliefsAndAttitude ? String(char.beliefsAndAttitude) : undefined,
          informationAccess: char.informationAccess ? String(char.informationAccess) : undefined,
          isManual: typeof char.isManual === 'boolean' ? char.isManual : undefined,
          relevance: clampEntityRelevance(char.relevance),
          mainImageResourceId:
            typeof char.mainImageResourceId === 'string' ? char.mainImageResourceId : null,
        }));

        setAllCharacters(charactersData);

        const foundCharacter = charactersData.find(
          (char) => char.id === characterId
        );

        if (!foundCharacter) {
          setError('Personaje no encontrado');
        } else {
          setCharacter(foundCharacter);
          setEditNameValue(foundCharacter.name);
          setEditDescriptionValue(foundCharacter.description || '');
          setEditNotesValue(foundCharacter.userNotes || '');
        }
      } catch (err) {
        console.error('Error fetching character:', err);
        setError('Error al cargar el personaje');
      } finally {
        setLoading(false);
      }
    };

    fetchCharacter();
  }, [token, documentId, characterId, masterKey]);

  useEffect(() => {
    if (character) {
      setEditNameValue(character.name);
      setEditDescriptionValue(character.description || '');
      setEditNotesValue(character.userNotes || '');
    }
  }, [character?.id]);

  const relatedEntities = useMemo(() => {
    if (!characterId || !documentId) return { linksTo: [], referencedIn: [] };
    const entityLookup = new Map<string, MentionableEntity>();
    for (const e of mentionableEntities) {
      const key = `${e.type}:${e.id}:${e.documentId ?? ''}`;
      entityLookup.set(key, e);
    }
    const linksTo: Array<{ id: string; type: string; label: string; documentId?: string }> = [];
    const referencedIn: Array<{ id: string; type: string; label: string; documentId?: string }> = [];
    for (const ref of entityRefs) {
      if (ref.sourceType === 'character' && ref.sourceId === characterId && ref.sourceDocumentId === documentId) {
        const key = `${ref.targetType}:${ref.targetId}:${ref.targetDocumentId ?? ''}`;
        const entity = entityLookup.get(key);
        if (entity) {
          linksTo.push({ id: ref.targetId, type: ref.targetType, label: entity.label, documentId: ref.targetDocumentId ?? undefined });
        }
      }
      if (ref.targetType === 'character' && ref.targetId === characterId && ref.targetDocumentId === documentId) {
        const key = `${ref.sourceType}:${ref.sourceId}:${ref.sourceDocumentId ?? ''}`;
        const entity = entityLookup.get(key);
        if (entity) {
          referencedIn.push({ id: ref.sourceId, type: ref.sourceType, label: entity.label, documentId: ref.sourceDocumentId ?? undefined });
        }
      }
    }
    const dedupe = (arr: typeof linksTo) => {
      const seen = new Set<string>();
      return arr.filter((x) => {
        const k = `${x.type}:${x.id}:${x.documentId ?? ''}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    };
    return { linksTo: dedupe(linksTo), referencedIn: dedupe(referencedIn) };
  }, [characterId, documentId, entityRefs, mentionableEntities]);

  useEffect(() => {
    if (scrollToSection !== 'related_entities') {
      return;
    }
    if (relatedEntities.linksTo.length === 0 && relatedEntities.referencedIn.length === 0) {
      return;
    }
    const frameId = window.requestAnimationFrame(() => {
      relatedEntitiesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [scrollToSection, relatedEntities]);

  const handleRemoveLinksTo = useCallback(
    async (entity: { type: string; id: string; documentId?: string }): Promise<void> => {
      if (!characterId || !documentId) return;
      const newDesc = removeMentionFromHtml(editDescriptionValue || '', entity);
      const newNotes = removeMentionFromHtml(editNotesValue || '', entity);
      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}/characters/${characterId}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}/characters/${characterId}`;
      try {
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            name: character?.name ?? editNameValue.trim(),
            description: newDesc.trim() || undefined,
            userNotes: newNotes.trim() || undefined,
          }),
        });
        if (!response.ok) throw new Error('Failed to update');
        const data = await response.json();
        setCharacter((prev) =>
          prev
            ? { ...prev, description: data.character.description, userNotes: data.character.userNotes }
            : prev
        );
        setEditDescriptionValue(data.character.description || '');
        setEditNotesValue(data.character.userNotes || '');
        setEntityRefsRefreshTrigger((p) => p + 1);
      } catch {
        showErrorToast(t('Failed to remove entity reference'));
      }
    },
    [
      characterId,
      documentId,
      editDescriptionValue,
      editNotesValue,
      editNameValue,
      character?.name,
      masterKey,
      token,
      t,
      showErrorToast,
    ]
  );

  const handleRemoveReferencedIn = useCallback(
    async (entity: { type: string; id: string; documentId?: string }): Promise<void> => {
      const projectIdToUse = projectId ?? resolvedProjectId;
      if (!token || !projectIdToUse || !characterId || !documentId) return;
      try {
        const res = await fetch(`${API_BASE}/api/projects/${projectIdToUse}/entity-references/remove`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sourceType: entity.type,
            sourceId: entity.id,
            sourceDocumentId: entity.documentId ?? null,
            targetType: 'character',
            targetId: characterId,
            targetDocumentId: documentId,
          }),
        });
        if (res.ok) {
          setEntityRefsRefreshTrigger((p) => p + 1);
        } else {
          showErrorToast(t('Failed to remove entity reference'));
        }
      } catch {
        showErrorToast(t('Failed to remove entity reference'));
      }
    },
    [characterId, documentId, projectId, resolvedProjectId, token, t, showErrorToast]
  );

  const saveCharacter = useCallback(async (): Promise<void> => {
    if (!characterId || !character || saving) return;
    const name = editNameValue.trim();
    const description = normalizeRichTextDescription(editDescriptionValue);
    const userNotes = normalizeRichTextDescription(editNotesValue);
    const hasChanges =
      name !== character.name ||
      (description || '') !== (character.description || '') ||
      (userNotes || '') !== (character.userNotes || '');
    if (!hasChanges) {
      setEditingName(false);
      if (isModal) {
        onClose?.();
      }
      return;
    }
    try {
      setSaving(true);
      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}/characters/${characterId}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}/characters/${characterId}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          name,
          description: description || undefined,
          userNotes: userNotes || undefined,
        }),
      });
      if (!response.ok) throw new Error('Failed to update character');
      const data = await response.json();
      const savedCharacter: Character = {
        ...character,
        name: data.character.name,
        description: data.character.description,
        userNotes: data.character.userNotes,
      };
      setCharacter(savedCharacter);
      setEditNameValue(savedCharacter.name);
      setEditDescriptionValue(savedCharacter.description || '');
      setEditNotesValue(savedCharacter.userNotes || '');
      setEditingName(false);
      onUpdated?.(savedCharacter);
      void dispatchMentionableEntitiesChangedForDocument(
        documentId,
        token ?? null,
        masterKey,
        projectId ?? resolvedProjectId,
      );
      if (isModal) {
        onClose?.();
      }
    } catch (err) {
      console.error('Error saving character:', err);
      setError(t('failed_to_create_character') || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  }, [
    characterId,
    character,
    editNameValue,
    editDescriptionValue,
    editNotesValue,
    documentId,
    masterKey,
    token,
    saving,
    t,
    isModal,
    onClose,
    onUpdated,
    projectId,
    resolvedProjectId,
  ]);

  const cancelEditing = useCallback((): void => {
    if (character) {
      setEditNameValue(character.name);
      setEditDescriptionValue(character.description || '');
      setEditNotesValue(character.userNotes || '');
      setEditingName(false);
    }
    if (isModal) {
      onClose?.();
    }
  }, [character, isModal, onClose]);

  const saveCharacterName = useCallback(
    async (name: string, rewriteMentions: boolean): Promise<boolean> => {
      if (!characterId || !character || !documentId || saving) {
        return false;
      }
      try {
        setSaving(true);
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/characters/${characterId}?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/characters/${characterId}`;
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ name, rewriteMentions }),
        });
        if (!response.ok) {
          throw new Error('Failed to update character');
        }
        const data = await response.json();
        const savedCharacter: Character = {
          ...character,
          name: data.character.name,
        };
        setCharacter(savedCharacter);
        setEditNameValue(savedCharacter.name);
        setAllCharacters((prev) =>
          prev.map((c) => (c.id === characterId ? { ...c, name: savedCharacter.name } : c)),
        );
        onUpdated?.(savedCharacter);
        void dispatchMentionableEntitiesChangedForDocument(
          documentId,
          token ?? null,
          masterKey,
          projectId ?? resolvedProjectId,
        );
        return true;
      } catch (err) {
        console.error('Error saving character name:', err);
        setEditNameValue(character.name);
        showErrorToast(t('failed_to_create_character') || 'Error al guardar');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [
      characterId,
      character,
      documentId,
      saving,
      masterKey,
      token,
      onUpdated,
      projectId,
      resolvedProjectId,
      showErrorToast,
      t,
    ],
  );

  const persistCharacterName = useCallback(
    async (nextName: string): Promise<boolean> => {
      if (!characterId || !character || !documentId || saving) {
        return false;
      }
      const name = nextName.trim();
      if (!name || name === character.name) {
        return false;
      }
      try {
        const impact = await fetchMentionRenameImpact({
          documentId,
          entityKind: 'characters',
          entityId: characterId,
          token: token ?? null,
          masterKey,
        });
        if (impact.sources.length === 0) {
          return saveCharacterName(name, false);
        }
        setPendingRenameName(name);
        setRenameImpactSources(impact.sources);
        setRenameImpactOpen(true);
        setEditNameValue(character.name);
        return false;
      } catch (err) {
        console.error('Error checking character mention rename impact:', err);
        showErrorToast(t('failed_to_load_mention_rename_impact'));
        setEditNameValue(character.name);
        return false;
      }
    },
    [
      characterId,
      character,
      documentId,
      saving,
      token,
      masterKey,
      saveCharacterName,
      showErrorToast,
      t,
    ],
  );

  const confirmCharacterRenameWithMentions = useCallback(async (): Promise<void> => {
    if (!pendingRenameName) {
      return;
    }
    setRenameImpactLoading(true);
    try {
      const saved = await saveCharacterName(pendingRenameName, true);
      if (saved) {
        showFieldSavedToast(t('name'));
        showToast(t('mention_rename_updated'));
      }
    } finally {
      setRenameImpactLoading(false);
      setPendingRenameName(null);
      setRenameImpactSources([]);
    }
  }, [pendingRenameName, saveCharacterName, showFieldSavedToast, showToast, t]);

  const deleteCharacter = useCallback(async (): Promise<void> => {
    if (!characterId || !documentId || deleting) return;
    try {
      setDeleting(true);
      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}/characters/${characterId}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}/characters/${characterId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to delete character');
      void dispatchMentionableEntitiesChangedForDocument(
        documentId,
        token ?? null,
        masterKey,
        projectId ?? resolvedProjectId,
      );
      onDeleted?.(characterId);
      onClose?.();
      if (!isModal) {
        const listUrl = projectId
          ? `/projects/${projectId}/documents/${documentId}/characters`
          : `/documents/${documentId}/characters`;
        navigate(listUrl);
      }
    } catch (err) {
      console.error('Error deleting character:', err);
      showErrorToast(t('failed_to_delete_character'));
    } finally {
      setDeleting(false);
    }
  }, [
    characterId,
    documentId,
    deleting,
    masterKey,
    token,
    projectId,
    resolvedProjectId,
    onDeleted,
    onClose,
    isModal,
    navigate,
    t,
    showErrorToast,
  ]);

  const applyAnalysisResult = useCallback(
    (updatedCharacter: Character): void => {
      setCharacter((prev) => (prev ? { ...prev, ...updatedCharacter } : prev));
      setEditDescriptionValue(updatedCharacter.description || '');
      setEditNotesValue(updatedCharacter.userNotes || '');
      setAllCharacters((prev) =>
        prev.map((c) => (c.id === updatedCharacter.id ? { ...c, ...updatedCharacter } : c))
      );
      setEntityRefsRefreshTrigger((p) => p + 1);
      if (documentId) {
        void dispatchMentionableEntitiesChangedForDocument(
          documentId,
          token ?? null,
          masterKey,
          projectId ?? resolvedProjectId,
        );
      }
    },
    [documentId, token, masterKey, projectId, resolvedProjectId]
  );

  const handleAnalyzeCharacter = useCallback(async (): Promise<void> => {
    if (analyzing || !characterId || !documentId) return;

    const abortController = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    try {
      setAnalyzing(true);
      setError(null);
      const authToken = shareToken || token;
      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}/characters/${characterId}/analyze?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}/characters/${characterId}/analyze`;

      timeoutId = setTimeout(() => {
        if (!abortController.signal.aborted) {
          abortController.abort();
        }
      }, 120000);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...(authToken && !masterKey ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        signal: abortController.signal,
      });

      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      if (!response.ok) {
        throw new Error('Failed to analyze character');
      }

      const data = await response.json();
      applyAnalysisResult(data.character as Character);
    } catch (err) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (err instanceof Error && err.name === 'AbortError') {
        setError(t('analysis_timed_out'));
      } else {
        console.error('Error analyzing character:', err);
        setError(t('failed_to_analyze_character'));
      }
    } finally {
      setAnalyzing(false);
    }
  }, [
    analyzing,
    characterId,
    documentId,
    shareToken,
    token,
    masterKey,
    applyAnalysisResult,
    t,
  ]);

  const persistCharacterField = useCallback(
    async (
      field: EditableCharacterField,
      value: string | Record<string, string>,
    ): Promise<boolean> => {
      if (!characterId || !character || !documentId || savingAnalysisField) {
        return false;
      }

      const previousCharacter = character;
      const nextCharacter: Character = {
        ...character,
        [field]: value,
      };

      if (JSON.stringify(nextCharacter[field]) === JSON.stringify(previousCharacter[field])) {
        return false;
      }

      setCharacter(nextCharacter);
      setSavingAnalysisField(true);

      try {
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/characters/${characterId}?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/characters/${characterId}`;
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ [field]: value }),
        });

        if (!response.ok) {
          throw new Error('Failed to update character');
        }

        const data = await response.json();
        setCharacter((prev) => (prev ? { ...prev, ...data.character } : prev));
        setAllCharacters((prev) =>
          prev.map((c) => (c.id === characterId ? { ...c, ...data.character } : c)),
        );
        return true;
      } catch (err) {
        setCharacter(previousCharacter);
        console.error('Error updating character field:', err);
        showErrorToast(t('failed_to_create_character'));
        return false;
      } finally {
        setSavingAnalysisField(false);
      }
    },
    [
      characterId,
      character,
      documentId,
      savingAnalysisField,
      masterKey,
      token,
      showErrorToast,
      t,
    ],
  );

  const persistCharacterMainImage = useCallback(
    async (resourceId: string | null): Promise<boolean> => {
      if (!characterId || !character || !documentId || savingAnalysisField) {
        return false;
      }
      const previousId = character.mainImageResourceId ?? null;
      if (previousId === resourceId) {
        return true;
      }

      const previousCharacter = character;
      setCharacter({ ...character, mainImageResourceId: resourceId });
      setSavingAnalysisField(true);
      try {
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/characters/${characterId}?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/characters/${characterId}`;
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ mainImageResourceId: resourceId }),
        });
        if (!response.ok) {
          throw new Error('Failed to update main image');
        }
        const data = await response.json();
        const savedId =
          typeof data.character?.mainImageResourceId === 'string'
            ? data.character.mainImageResourceId
            : null;
        setCharacter((prev) => (prev ? { ...prev, mainImageResourceId: savedId } : prev));
        setAllCharacters((prev) =>
          prev.map((entry) =>
            entry.id === characterId ? { ...entry, mainImageResourceId: savedId } : entry,
          ),
        );
        showToast(
          savedId ? t('character_main_image_set') : t('character_main_image_cleared'),
        );
        return true;
      } catch (err) {
        setCharacter(previousCharacter);
        console.error('Error updating character main image:', err);
        showErrorToast(t('character_main_image_failed'));
        return false;
      } finally {
        setSavingAnalysisField(false);
      }
    },
    [
      characterId,
      character,
      documentId,
      savingAnalysisField,
      masterKey,
      token,
      showToast,
      showErrorToast,
      t,
    ],
  );

  const persistCharacterRelevance = useCallback(
    async (nextRelevance: number | undefined): Promise<void> => {
      if (!characterId || !character || !documentId || savingAnalysisField) {
        return;
      }

      const previousRelevance = clampEntityRelevance(character.relevance);
      const nextClamped = clampEntityRelevance(nextRelevance);
      if (previousRelevance === nextClamped) {
        return;
      }

      const previousCharacter = character;
      const nextCharacter: Character = {
        ...character,
        relevance: nextClamped,
      };
      setCharacter(nextCharacter);
      setSavingAnalysisField(true);

      try {
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/characters/${characterId}?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/characters/${characterId}`;
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ relevance: nextClamped ?? null }),
        });

        if (!response.ok) {
          throw new Error('Failed to update character relevance');
        }

        const data = await response.json();
        const savedCharacter: Character = {
          ...character,
          ...data.character,
          relevance: clampEntityRelevance(data.character?.relevance),
        };
        setCharacter(savedCharacter);
        setAllCharacters((prev) =>
          prev.map((c) => (c.id === characterId ? { ...c, ...savedCharacter } : c)),
        );
        onUpdated?.(savedCharacter);
      } catch (err) {
        setCharacter(previousCharacter);
        console.error('Error updating character relevance:', err);
        showErrorToast(t('failed_to_create_character'));
      } finally {
        setSavingAnalysisField(false);
      }
    },
    [
      characterId,
      character,
      documentId,
      savingAnalysisField,
      masterKey,
      token,
      onUpdated,
      showErrorToast,
      t,
    ],
  );

  const currentIndex = allCharacters.findIndex((char) => char.id === characterId);
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < allCharacters.length - 1;

  const navigateToCharacter = useCallback((direction: 'prev' | 'next'): void => {
    if (!documentId || isModal) return;

    let targetIndex: number;
    if (direction === 'prev') {
      targetIndex = currentIndex - 1;
    } else {
      targetIndex = currentIndex + 1;
    }

    if (targetIndex < 0 || targetIndex >= allCharacters.length) return;

    const targetCharacter = allCharacters[targetIndex];
    const url = projectId ? `/projects/${projectId}/documents/${documentId}/characters/${targetCharacter.id}` : `/documents/${documentId}/characters/${targetCharacter.id}`;
    const searchParams = new URLSearchParams(window.location.search);
    const masterKeyParam = searchParams.get('masterKey');
    const shareTokenParam = searchParams.get('shareToken');
    const finalUrl = masterKeyParam || shareTokenParam
      ? `${url}?${masterKeyParam ? `masterKey=${encodeURIComponent(masterKeyParam)}` : ''}${shareTokenParam ? `${masterKeyParam ? '&' : ''}shareToken=${encodeURIComponent(shareTokenParam)}` : ''}`
      : url;
    navigate(finalUrl);
  }, [documentId, currentIndex, allCharacters, navigate, isModal]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) {
        return;
      }

      if (e.key === 'ArrowLeft' && hasPrevious) {
        e.preventDefault();
        navigateToCharacter('prev');
      } else if (e.key === 'ArrowRight' && hasNext) {
        e.preventDefault();
        navigateToCharacter('next');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [hasPrevious, hasNext, navigateToCharacter]);

  if (isCreateMode) {
    return (
      <EntityDetailModalLayout
        className="h-auto flex-none"
        header={<EntityDetailModalTitle>{t('add_character')}</EntityDetailModalTitle>}
        footer={
          <DialogFooter className="gap-2 sm:justify-end">
            <CancelButton
              onClick={() => {
                setCreateName('');
                setCreateUserNotes('');
                setCreateRelevance(
                  defaultCreateEntityRelevance(
                    documentId ? readLastCreatedCharacterRelevance(documentId) : undefined
                  )
                );
                onClose?.();
              }}
            >
              {t('cancel')}
            </CancelButton>
            <Button
              disabled={!createName.trim() || creating}
              dialogPrimary
              className="inline-flex items-center gap-1.5"
              onClick={async () => {
                if (!createName.trim() || creating || !documentId) return;
                try {
                  setCreating(true);
                  const url = masterKey
                    ? `${API_BASE}/api/documents/${documentId}/characters?masterKey=${encodeURIComponent(masterKey)}`
                    : `${API_BASE}/api/documents/${documentId}/characters`;
                  const relevance = defaultCreateEntityRelevance(createRelevance);
                  const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify({
                      name: createName.trim(),
                      userNotes: normalizeRichTextDescription(createUserNotes),
                      relevance,
                    }),
                  });
                  if (!response.ok) throw new Error('Failed to create character');
                  const data = await readJsonWithOnboarding<{ character: Character }>(response);
                  const newCharacter = data.character;
                  writeLastCreatedCharacterRelevance(
                    documentId,
                    clampEntityRelevance(newCharacter.relevance) ?? relevance
                  );
                  setCreateName('');
                  setCreateUserNotes('');
                  setCreateRelevance(
                    defaultCreateEntityRelevance(readLastCreatedCharacterRelevance(documentId))
                  );
                  onCreate?.(newCharacter);
                  if (!onCreate) {
                    void dispatchMentionableEntitiesChangedForDocument(
                      documentId,
                      token ?? null,
                      masterKey,
                      projectId ?? resolvedProjectId,
                    );
                  }
                  onClose?.();
                } catch (err) {
                  console.error('Error creating character:', err);
                  setError(t('failed_to_create_character') || 'Error al crear el personaje');
                } finally {
                  setCreating(false);
                }
              }}
            >
              {creating ? (
                t('adding')
              ) : (
                <>
                  {t('add')}
                  <DialogPrimaryShortcut className="ml-0.5" />
                </>
              )}
            </Button>
          </DialogFooter>
        }
      >
        <div className="p-4">
          <div style={{ marginBottom: '1rem' }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '500',
                marginBottom: '0.5rem',
                color: 'var(--foreground)',
              }}
            >
              {t('name')} *
            </label>
            <Input
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              placeholder={t('character_name_placeholder')}
              className="bg-background"
              style={{ width: '100%' }}
              autoFocus
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '500',
                marginBottom: '0.5rem',
                color: 'var(--foreground)',
              }}
            >
              {t('entity_relevance')}
            </label>
            <EntityRelevanceEditor
              value={createRelevance}
              disabled={creating}
              onChange={(nextRelevance) => {
                setCreateRelevance(defaultCreateEntityRelevance(nextRelevance));
              }}
            />
          </div>
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '500',
                marginBottom: '0.5rem',
                color: 'var(--foreground)',
              }}
            >
              {t('notes')}
            </label>
            <RichTextField
              value={createUserNotes}
              onChange={setCreateUserNotes}
              placeholder={t('character_notes_placeholder')}
              minHeight="150px"
              maxHeight="40vh"
              mentionableEntities={mentionableEntities}
            />
            <p
              style={{
                margin: '0.5rem 0 0 0',
                fontSize: '0.75rem',
                color: 'var(--muted-foreground)',
                lineHeight: '1.4',
              }}
            >
              {t('user_notes_analysis_safe_help')}
            </p>
          </div>
        </div>
      </EntityDetailModalLayout>
    );
  }

  if (loading) {
    if (isModal) {
      return (
        <div className="bg-background text-foreground" style={{ padding: '2rem', textAlign: 'center' }}>
          <div>{t('loading')}</div>
        </div>
      );
    }
    return (
      <>
        <div style={{ height: '56px' }} />
        <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center' }}>{t('loading')}</div>
        </div>
      </>
    );
  }

  if (error || !character) {
    if (isModal) {
      return (
        <div className="bg-background text-foreground" style={{ padding: '2rem' }}>
          <div style={{ color: '#ef4444', textAlign: 'center' }}>
            {error || 'Personaje no encontrado'}
          </div>
          {onClose && (
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <Button type="button" onClick={onClose}>
                Cerrar
              </Button>
            </div>
          )}
        </div>
      );
    }
    return (
      <>
        <div style={{ height: '56px' }} />
        <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ color: '#ef4444', textAlign: 'center' }}>
            {error || 'Personaje no encontrado'}
          </div>
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <Button asChild>
              <Link
                to={projectId ? `/projects/${projectId}/documents/${documentId}/characters` : `/documents/${documentId}/characters`}
              >
                Volver a personajes
              </Link>
            </Button>
          </div>
        </div>
      </>
    );
  }

  const editTooltip = t('inline_editable_click_to_edit');
  const canInlineEdit = !shareToken;

  const renderEditableAnalysisField = (
    field: Exclude<EditableCharacterField, 'relationships'>,
    label: string,
    options?: { multiline?: boolean },
  ): React.ReactNode => {
    if (!character) {
      return null;
    }
    const rawValue = character[field] ?? '';
    const value = formatAnalysisPlainText(rawValue);
    if (!value && !canInlineEdit) {
      return null;
    }

    return (
      <div style={{ marginBottom: '1.5rem' }}>
        <span
          style={{
            fontSize: '0.75rem',
            fontWeight: '600',
            color: 'var(--muted-foreground)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {label}
        </span>
        <div style={{ marginTop: '0.5rem' }}>
          <InlineEditableText
            value={value}
            onCommit={(next) => persistCharacterField(field, next)}
            savedFieldLabel={label}
            readOnly={!canInlineEdit}
            disabled={savingAnalysisField}
            allowEmpty
            multiline={options?.multiline ?? true}
            rows={options?.multiline === false ? undefined : 4}
            placeholder={label}
            tooltip={editTooltip}
            className="w-full font-normal text-base text-foreground leading-relaxed"
            displayClassName="w-full"
          />
        </div>
      </div>
    );
  };

  const renderRelationshipsField = (): React.ReactNode => {
    if (!character) {
      return null;
    }
    const { asObject, asString } = parseRelationships(character.relationships);
    if (!asObject && !asString && !canInlineEdit) {
      return null;
    }

    return (
      <div style={{ marginBottom: '1.5rem' }}>
        <span
          style={{
            fontSize: '0.75rem',
            fontWeight: '600',
            color: 'var(--muted-foreground)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {t('character_relationships')}
        </span>
        <div style={{ marginTop: '0.5rem' }}>
          {asObject ? (
            <dl style={{ margin: 0, padding: 0 }}>
              {Object.entries(asObject).map(([name, description], index, entries) => (
                <div
                  key={`${name}-${index}`}
                  style={{
                    marginBottom: index < entries.length - 1 ? '0.75rem' : 0,
                  }}
                >
                  <dt style={{ marginBottom: '0.25rem' }}>
                    <InlineEditableText
                      value={name}
                      onCommit={(nextName) => {
                        const trimmed = nextName.trim();
                        if (!trimmed || trimmed === name) {
                          return false;
                        }
                        const nextRelationships = { ...asObject };
                        const existingDescription = nextRelationships[name];
                        delete nextRelationships[name];
                        nextRelationships[trimmed] = existingDescription;
                        return persistCharacterField('relationships', nextRelationships);
                      }}
                      savedFieldLabel={t('character_relationships')}
                      readOnly={!canInlineEdit}
                      disabled={savingAnalysisField}
                      placeholder={t('character_relationships')}
                      tooltip={editTooltip}
                      className="font-semibold text-base text-foreground"
                    />
                  </dt>
                  <dd style={{ margin: 0, marginLeft: '1rem' }}>
                    <InlineEditableText
                      value={formatAnalysisPlainText(description)}
                      onCommit={(nextDescription) =>
                        persistCharacterField('relationships', {
                          ...asObject,
                          [name]: nextDescription,
                        })
                      }
                      savedFieldLabel={t('character_relationships')}
                      readOnly={!canInlineEdit}
                      disabled={savingAnalysisField}
                      allowEmpty
                      multiline
                      rows={3}
                      placeholder={t('character_relationships')}
                      tooltip={editTooltip}
                      className="w-full font-normal text-base text-foreground leading-relaxed"
                      displayClassName="w-full"
                    />
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <InlineEditableText
              value={formatAnalysisPlainText(asString ?? '')}
              onCommit={(next) => persistCharacterField('relationships', next)}
              savedFieldLabel={t('character_relationships')}
              readOnly={!canInlineEdit}
              disabled={savingAnalysisField}
              allowEmpty
              multiline
              rows={4}
              placeholder={t('character_relationships')}
              tooltip={editTooltip}
              className="w-full font-normal text-base text-foreground leading-relaxed"
              displayClassName="w-full"
            />
          )}
        </div>
      </div>
    );
  };

  const characterViewToggle =
    characterId && character && documentId ? (
      <Tabs
        value={panelView}
        onValueChange={(value) => switchPanelView(value as CharacterPanelView)}
      >
        <TabsList className="h-9">
          <TabsTrigger value="detail">{t('character_detail_view')}</TabsTrigger>
          <TabsTrigger value="chat" title={t('character_chat_open_tooltip')}>
            {t('character_chat')}
          </TabsTrigger>
        </TabsList>
      </Tabs>
    ) : null;

  const linkedImagesCarousel =
    characterId && character && documentId && token && (projectId ?? resolvedProjectId) ? (
      <LinkedImageResourcesCarousel
        projectId={projectId ?? resolvedProjectId ?? ''}
        entityType="character"
        entityId={characterId}
        documentId={documentId}
        token={token}
        refreshKey={entityRefsRefreshTrigger}
        uploadEnabled={canInlineEdit}
        mainImageResourceId={character.mainImageResourceId}
        onMainImageChange={canInlineEdit ? persistCharacterMainImage : undefined}
        onLinked={() => setEntityRefsRefreshTrigger((value) => value + 1)}
      />
    ) : null;

  const modalHeader =
    characterId && character ? (
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="min-w-0 flex-1" data-dialog-skip-autofocus>
            <InlineEditableText
              value={editNameValue}
              editing={editingName}
              onEditingChange={setEditingName}
              onChange={setEditNameValue}
              onCommit={persistCharacterName}
              savedFieldLabel={t('name')}
              disabled={saving || panelView === 'chat'}
              placeholder={character.name}
              as="h2"
              skipDialogAutofocus
              className="text-foreground"
              displayClassName="m-0"
            />
          </div>
          {character.isManual ? (
            <Badge variant="secondary" className="shrink-0 text-[0.625rem] uppercase">
              {t('manual')}
            </Badge>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <AnalyzeButton
            onClick={() => {
              void handleAnalyzeCharacter();
            }}
            isAnalyzing={analyzing}
            className={panelView === 'chat' ? 'invisible pointer-events-none mr-0' : 'mr-0'}
            tooltip={t('analyze_character_tooltip')}
            matchLabelWidth={t('reanalyze')}
          >
            {analyzing
              ? t('analyzing')
              : character.inconsistencies || character.improvements
                ? t('reanalyze')
                : t('analyze')}
          </AnalyzeButton>
          {characterViewToggle}
        </div>
      </div>
    ) : null;

  const detailFooter =
    characterId && (isModal || character) ? (
      <EditDialogFooter
        isEditing
        isSaving={saving || deleting}
        onCancel={cancelEditing}
        onSave={() => {
          void saveCharacter();
        }}
        onDelete={() => setDeleteConfirmOpen(true)}
        deleteDisabled={deleting}
      />
    ) : null;

  const detailFields = (
    <>
        {!isModal && (
          <div className="mb-6 flex items-center gap-2">
            <h2
              style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                margin: 0,
                color: 'var(--foreground)',
              }}
            >
              {character.name}
            </h2>
            {character.isManual ? (
              <Badge variant="secondary" className="shrink-0 text-[0.625rem] uppercase">
                {t('manual')}
              </Badge>
            ) : null}
          </div>
        )}

        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end">
          <div
            className="min-w-0 flex-1"
            style={{ padding: '1rem', backgroundColor: 'var(--muted)', border: '1px solid var(--border)', borderRadius: '6px' }}
          >
            <span
              style={{
                fontSize: '0.75rem',
                fontWeight: '600',
                color: 'var(--muted-foreground)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {t('notes')}
            </span>
            <div style={{ marginTop: '0.5rem' }}>
              <RichTextField
                value={editNotesValue || ''}
                onChange={setEditNotesValue}
                mentionableEntities={mentionableEntities}
                placeholder={t('character_notes_placeholder')}
                minHeight="120px"
                maxHeight="40vh"
                autoFocus={isModal}
              />
            </div>
            <p
              style={{
                margin: '0.5rem 0 0 0',
                fontSize: '0.75rem',
                color: 'var(--muted-foreground)',
                lineHeight: '1.4',
              }}
            >
              {t('user_notes_analysis_safe_help')}
            </p>
          </div>
          {linkedImagesCarousel ? (
            <div className="mx-auto shrink-0 sm:mx-0">{linkedImagesCarousel}</div>
          ) : null}
        </div>

        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start">
          <div className="shrink-0">
            <span
              style={{
                fontSize: '0.75rem',
                fontWeight: '600',
                color: 'var(--muted-foreground)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {t('entity_relevance')}
            </span>
            <div style={{ marginTop: '0.5rem' }}>
              {canInlineEdit ? (
                <EntityRelevanceEditor
                  value={character.relevance}
                  disabled={savingAnalysisField}
                  onChange={(nextRelevance) => {
                    void persistCharacterRelevance(nextRelevance);
                  }}
                />
              ) : (
                <EntityRelevanceDisplay value={character.relevance} size="detailed" />
              )}
            </div>
          </div>
          <div className="min-w-0 flex-1 [&>div]:mb-0">
            {renderEditableAnalysisField('role', t('character_role'), { multiline: false })}
          </div>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: '600',
              color: 'var(--muted-foreground)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {t('description')}
          </span>
          {isModal ? (
            <div style={{ marginTop: '0.5rem' }}>
              <RichTextField
                value={editDescriptionValue || ''}
                onChange={setEditDescriptionValue}
                mentionableEntities={mentionableEntities}
                minHeight="120px"
                maxHeight="40vh"
              />
            </div>
          ) : (
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '1rem', color: 'var(--foreground)', lineHeight: '1.6' }}>
              {character.description ? stripHtml(character.description) : '-'}
            </p>
          )}
        </div>

        {renderEditableAnalysisField('appearance', t('character_appearance'))}
        {renderEditableAnalysisField('personality', t('character_personality'))}
        {renderEditableAnalysisField('background', t('character_background'))}
        {renderEditableAnalysisField('speechStyle', t('character_speech_style'))}
        {renderEditableAnalysisField('mannerisms', t('character_mannerisms'))}
        {renderEditableAnalysisField('beliefsAndAttitude', t('character_beliefs_and_attitude'))}
        {renderEditableAnalysisField('informationAccess', t('character_information_access'))}
        {renderRelationshipsField()}
        {renderEditableAnalysisField('notes', t('character_analysis_notes'))}

        {characterId && !isModal && (
          <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'var(--muted)', border: '1px solid var(--border)', borderRadius: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span
                style={{
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: 'var(--muted-foreground)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {t('analysis')}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <AnalyzeButton
                  onClick={() => {
                    void handleAnalyzeCharacter();
                  }}
                  isAnalyzing={analyzing}
                  className="mr-0"
                  tooltip={t('analyze_character_tooltip')}
                >
                  {analyzing
                    ? t('analyzing')
                    : character.inconsistencies || character.improvements
                      ? t('reanalyze')
                      : t('analyze')}
                </AnalyzeButton>
              </div>
            </div>
            {!character.inconsistencies && !character.improvements && (
              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--muted-foreground)' }}>
                {t('character_no_analysis_yet')}
              </p>
            )}
          </div>
        )}

        {character.inconsistencies ? (
          <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'hsl(var(--destructive) / 0.12)', border: '1px solid hsl(var(--destructive) / 0.4)', borderRadius: '6px' }}>
            <span
              style={{
                fontSize: '0.75rem',
                fontWeight: '600',
                color: 'hsl(var(--destructive))',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {t('character_inconsistencies')}
            </span>
            <div style={{ marginTop: '0.5rem' }}>
              <InlineEditableText
                value={formatAnalysisPlainText(character.inconsistencies)}
                onCommit={(next) => persistCharacterField('inconsistencies', next)}
                savedFieldLabel={t('character_inconsistencies')}
                readOnly={!canInlineEdit}
                disabled={savingAnalysisField}
                allowEmpty
                multiline
                rows={4}
                placeholder={t('character_inconsistencies')}
                tooltip={editTooltip}
                className="w-full font-normal text-base text-foreground leading-relaxed"
                displayClassName="w-full"
              />
            </div>
          </div>
        ) : null}

        {character.improvements ? (
          <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'hsl(142 76% 36% / 0.08)', border: '1px solid hsl(142 76% 36% / 0.35)', borderRadius: '6px' }}>
            <span
              style={{
                fontSize: '0.75rem',
                fontWeight: '600',
                color: 'hsl(142 76% 36%)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {t('character_improvements')}
            </span>
            <div style={{ marginTop: '0.5rem' }}>
              <InlineEditableText
                value={formatAnalysisPlainText(character.improvements)}
                onCommit={(next) => persistCharacterField('improvements', next)}
                savedFieldLabel={t('character_improvements')}
                readOnly={!canInlineEdit}
                disabled={savingAnalysisField}
                allowEmpty
                multiline
                rows={4}
                placeholder={t('character_improvements')}
                tooltip={editTooltip}
                className="w-full font-normal text-base text-foreground leading-relaxed"
                displayClassName="w-full"
              />
            </div>
          </div>
        ) : null}

        {(projectId ?? resolvedProjectId) && (relatedEntities.linksTo.length > 0 || relatedEntities.referencedIn.length > 0) && (
          <div ref={relatedEntitiesSectionRef}>
            <label style={{ fontSize: '0.875rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
              {t('related_entities')}
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {relatedEntities.linksTo.length > 0 && (
                <div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--muted-foreground)', display: 'block', marginBottom: '0.25rem' }}>
                    {t('links_to')}
                  </span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {relatedEntities.linksTo.map((e) => (
                      <RelatedEntityLink
                        key={`${e.type}-${e.id}-${e.documentId ?? ''}`}
                        entity={e}
                        projectId={projectId ?? resolvedProjectId ?? ''}
                        onRemove={() => handleRemoveLinksTo(e)}
                      />
                    ))}
                  </div>
                </div>
              )}
              {relatedEntities.referencedIn.length > 0 && (
                <div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--muted-foreground)', display: 'block', marginBottom: '0.25rem' }}>
                    {t('referenced_in')}
                  </span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {relatedEntities.referencedIn.map((e) => (
                      <RelatedEntityLink
                        key={`${e.type}-${e.id}-${e.documentId ?? ''}`}
                        entity={e}
                        projectId={projectId ?? resolvedProjectId ?? ''}
                        onRemove={() => handleRemoveReferencedIn(e)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
    </>
  );

  const chatPanel =
    characterId && documentId && character ? (
      <CharacterChat
        documentId={documentId}
        characterId={characterId}
        characterName={character.name}
        projectId={projectId}
        chromeContainer={isModal ? chatChromeEl : undefined}
        otherCharacters={allCharacters.map((entry) => ({
          id: entry.id,
          name: entry.name,
          relevance: entry.relevance,
        }))}
      />
    ) : null;

  const panelBody = (
    <div
      ref={panelBodyRef}
      className={
        panelView === 'chat'
          ? 'flex h-full min-h-0 flex-col overflow-hidden bg-background text-foreground'
          : 'bg-background p-8 text-foreground'
      }
    >
      <div className={panelView === 'detail' ? 'block' : 'hidden'} aria-hidden={panelView !== 'detail'}>
        {detailFields}
      </div>
      <div
        className={
          panelView === 'chat' ? 'flex h-full min-h-0 flex-1 flex-col overflow-hidden' : 'hidden'
        }
        aria-hidden={panelView !== 'chat'}
      >
        {chatPanel}
      </div>
    </div>
  );

  const content = isModal ? (
    <EntityDetailModalLayout
      overlayChrome={panelView === 'chat'}
      header={modalHeader}
      footer={
        <>
          <div className={panelView === 'detail' ? 'block' : 'hidden'}>{detailFooter}</div>
          <div
            ref={setChatChromeEl}
            className={panelView === 'chat' ? 'block' : 'hidden'}
          />
        </>
      }
    >
      {panelBody}
    </EntityDetailModalLayout>
  ) : (
    <div
      style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}
    >
      {!isModal && (
        <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ fontSize: '1.875rem', fontWeight: '600', margin: 0 }}>
            {documentTitle ? (
              <>
                <Link
                  to={projectId ? `/projects/${projectId}/documents/${documentId}` : `/documents/${documentId}`}
                  style={{
                    color: 'var(--scribane-black)',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLElement).style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLElement).style.textDecoration = 'none';
                  }}
                >
                  {documentTitle}
                </Link>
                {' / '}
                <Link
                  to={projectId ? `/projects/${projectId}/documents/${documentId}/characters` : `/documents/${documentId}/characters`}
                  style={{
                    color: 'var(--scribane-black)',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLElement).style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLElement).style.textDecoration = 'none';
                  }}
                >
                  Personajes
                </Link>
                {' / '}
                <span style={{ color: 'var(--foreground)' }}>{character.name}</span>
              </>
            ) : (
              <>
                <Link
                  to={projectId ? `/projects/${projectId}/documents/${documentId}/characters` : `/documents/${documentId}/characters`}
                  style={{
                    color: 'var(--scribane-black)',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLElement).style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLElement).style.textDecoration = 'none';
                  }}
                >
                  Personajes
                </Link>
                {' / '}
                <span style={{ color: 'var(--foreground)' }}>{character.name}</span>
              </>
            )}
          </h1>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {hasPrevious && !isModal && (
              <Button
                type="button"
                onClick={() => navigateToCharacter('prev')}
              >
                ← Anterior
              </Button>
            )}
            {hasNext && !isModal && (
              <Button
                type="button"
                onClick={() => navigateToCharacter('next')}
              >
                Siguiente →
              </Button>
            )}
            <Button asChild variant="outline">
              <Link
                to={projectId ? `/projects/${projectId}/documents/${documentId}/characters` : `/documents/${documentId}/characters`}
              >
                Volver a personajes
              </Link>
            </Button>
          </div>
        </div>
      )}


      <div
        style={{
          backgroundColor: 'var(--card)',
          border: '1px solid var(--border)',
          borderRadius: '8px',
          padding: '2rem',
        }}
      >
        {!isModal && characterId && character ? (
          <div className="mb-6 flex items-center justify-end">{characterViewToggle}</div>
        ) : null}
        <div className={panelView === 'detail' ? 'block' : 'hidden'} aria-hidden={panelView !== 'detail'}>
          {detailFields}
        </div>
        <div className={panelView === 'chat' ? 'block' : 'hidden'} aria-hidden={panelView !== 'chat'}>
          {chatPanel}
        </div>
        {detailFooter ? (
          <div
            className={`mt-6 ${panelView === 'detail' ? 'block' : 'invisible pointer-events-none'}`}
          >
            {detailFooter}
          </div>
        ) : null}
      </div>
    </div>
  );

  const deleteConfirmDialog = (
    <ConfirmDialog
      open={deleteConfirmOpen}
      onOpenChange={setDeleteConfirmOpen}
      title={t('delete_character')}
      description={t('confirm_delete_character')}
      confirmLabel={t('delete_character')}
      onConfirm={() => {
        void deleteCharacter();
      }}
    />
  );

  const renameImpactDialog = (
    <ConfirmDialog
      open={renameImpactOpen}
      onOpenChange={(open) => {
        setRenameImpactOpen(open);
        if (!open) {
          setPendingRenameName(null);
          setRenameImpactSources([]);
          if (character) {
            setEditNameValue(character.name);
          }
        }
      }}
      title={t('mention_rename_confirm_title')}
      description={t('mention_rename_confirm_description_character')
        .replace('{from}', character?.name ?? '')
        .replace('{to}', pendingRenameName ?? '')}
      confirmLabel={t('rename')}
      cancelLabel={t('cancel')}
      destructive={false}
      confirmLoading={renameImpactLoading}
      onConfirm={() => confirmCharacterRenameWithMentions()}
    >
      <MentionRenameImpactList
        sources={renameImpactSources}
        projectId={projectId ?? resolvedProjectId}
      />
    </ConfirmDialog>
  );

  if (isModal) {
    return (
      <>
        {content}
        {deleteConfirmDialog}
        {renameImpactDialog}
      </>
    );
  }

  return (
    <>
      <div style={{ height: '56px' }} />
      {content}
      {deleteConfirmDialog}
      {renameImpactDialog}
    </>
  );
};

